import { useExportHook, useExportProps } from './types';
import { useEffect, useMemo, useState } from 'react';
import { useCustomQuery } from '@gripp/shared-logic';
import { getDateFilter } from './dateFilterUtils';
import { useDataPlateColumns } from './useDataPlateColumns';

export const useActivityExport: useExportHook = (props: useExportProps) => {
  const addAllAssetInfo = !!props.addAllAssetInfo;
  const [rows, setRows] = useState([]);

  const { data, isLoading } = useCustomQuery({
    cacheKey: [
      'exportActivities',
      props.workspace.id,
      props.assetId,
      props.dateFilter,
      addAllAssetInfo,
    ],
    // Only request the asset description/details fields when the toggle is on.
    // This keeps the default export's query unchanged so it can't break against
    // an API that hasn't picked up the new schema fields yet.
    query: addAllAssetInfo ? EXPORT_ACTIVITIES_WITH_ASSET_INFO : EXPORT_ACTIVITIES,
    variables: {
      filter: {
        workspaceId: props.workspace.id,
        ...(props.assetId && { assetId: props.assetId }),
        ...getDateFilter(props.dateFilter),
      },
    },
    options: {
      enabled: props.download,
      gcTime: 10000,
      staleTime: 10000,
    },
  });

  // "Add all asset info" appends the asset's description/details plus the latest
  // submission of every one-time ("data plate") routine, exactly like the asset
  // export. The data plate is asset metadata surfaced as trailing columns — a
  // snapshot of one-time routines — and is distinct from the routine records the
  // activity export now includes as their own timeline rows.
  const dataPlate = useDataPlateColumns(
    props.workspace.id,
    addAllAssetInfo && props.download,
    // Scope the data-plate columns to this asset so the per-asset export only
    // shows one-time routines this asset actually has data for.
    props.assetId
  );

  const headers = useMemo(() => {
    const base = [
      { label: 'Asset ID', key: 'assetId' },
      { label: 'Asset Name', key: 'assetName' },
      ...(addAllAssetInfo
        ? [
            { label: 'Description', key: 'assetDescription' },
            { label: 'Details', key: 'assetDetails' },
          ]
        : []),
      { label: 'Activity ID', key: 'activityId' },
      { label: 'Activity Title', key: 'activityTitle' },
      { label: 'Activity Type', key: 'activityType' },
      { label: 'Activity Created By', key: 'activityCreatedBy' },
      { label: 'Activity Created Date', key: 'activityCreatedDate' },
      { label: 'Activity Created Time', key: 'activityCreatedTime' },
      { label: 'Activity Creation UTC', key: 'activityCreationUtc' },
      { label: 'Message ID', key: 'messageId' },
      { label: 'Message Text', key: 'messageText' },
      { label: 'Message Created By', key: 'messageCreatedBy' },
      { label: 'Message Created Date', key: 'messageCreatedDate' },
      { label: 'Message Created Time', key: 'messageCreatedTime' },
      { label: 'Message Creation UTC', key: 'messageCreationUtc' },
      { label: 'Groups', key: 'groups' },
      { label: 'Labels', key: 'labels' },
      { label: 'Marker', key: 'marker' },
    ];
    return addAllAssetInfo ? [...base, ...dataPlate.dataPlateHeaders] : base;
  }, [addAllAssetInfo, dataPlate.dataPlateHeaders]);

  useEffect(() => {
    if (!data) return;
    // Hold off until the data-plate columns are ready so the export isn't
    // triggered with empty "all asset info" columns (mirrors useAssetExport).
    if (addAllAssetInfo && dataPlate.isLoading) return;

    const exportRows = data.issue.exportActivity.data.map((item: any) => {
      const row = {
        ...item,
        ...(addAllAssetInfo
          ? dataPlate.valuesByAssetId[item.assetId] ?? {}
          : {}),
      };

      // Split activityCreatedAt
      if (item.activityCreatedAt) {
        const date = new Date(item.activityCreatedAt);
        row.activityCreatedDate = date.toLocaleDateString();
        row.activityCreatedTime = date.toLocaleTimeString();
        row.activityCreationUtc = date.toISOString();
      }

      // Split messageCreatedAt
      if (item.messageCreatedAt) {
        const date = new Date(item.messageCreatedAt);
        row.messageCreatedDate = date.toLocaleDateString();
        row.messageCreatedTime = date.toLocaleTimeString();
        row.messageCreationUtc = date.toISOString();
      }

      return row;
    });

    setRows(exportRows);
    props.onDataReady();
  }, [data, addAllAssetInfo, dataPlate]);

  return {
    headers,
    rows,
    isLoading: isLoading || (addAllAssetInfo && dataPlate.isLoading),
  };
};

const EXPORT_ACTIVITIES = `
  query Issue($filter: ExportActivityFilter) {
    issue {
      exportActivity(filter: $filter) {
        data {
          activityCreatedAt
          activityCreatedBy
          activityId
          activityTitle
          activityType
          assetId
          assetName
          messageCreatedAt
          messageCreatedBy
          messageId
          messageText
          groups
          labels
          marker
        }
      }
    }
  }
`;

// Variant requested only when "Add all asset info" is on — adds the asset
// description/details columns (the data-plate columns are merged client-side
// from useDataPlateColumns).
const EXPORT_ACTIVITIES_WITH_ASSET_INFO = `
  query Issue($filter: ExportActivityFilter) {
    issue {
      exportActivity(filter: $filter) {
        data {
          activityCreatedAt
          activityCreatedBy
          activityId
          activityTitle
          activityType
          assetId
          assetName
          assetDescription
          assetDetails
          messageCreatedAt
          messageCreatedBy
          messageId
          messageText
          groups
          labels
          marker
        }
      }
    }
  }
`;
